import { useState, useEffect } from "react";
import { Users, Clock, Calendar, MapPin } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_ORDER = { Monday: 0, Tuesday: 1, Wednesday: 2, Thursday: 3, Friday: 4, Saturday: 5, Sunday: 6 };

const ClassesOverview = () => {
  const [classGroups, setClassGroups] = useState([]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get(`${API}/classes`);
        const all = res.data || [];

        const grouped = {};
        for (const cls of all) {
          const key = cls.class_type || cls.class_name;
          if (!grouped[key]) {
            grouped[key] = {
              class_type: cls.class_type,
              class_name: cls.class_name,
              price: cls.price,
              min_age_months: cls.min_age_months,
              max_age_months: cls.max_age_months,
              duration_minutes: cls.duration_minutes,
              with_parent: cls.with_parent !== false,
              sessions: []
            };
          }
          grouped[key].sessions.push({
            day: cls.day_of_week,
            time: cls.time,
            location: cls.location_display
          });
        }

        const groups = Object.values(grouped).filter(g => g.sessions.length > 0);
        groups.sort((a, b) => a.min_age_months - b.min_age_months);
        setClassGroups(groups);
      } catch (err) {
        console.error("Failed to load classes", err);
      }
    };
    fetchClasses();
  }, []);

  const accents = ["red", "blue", "red", "blue"];
  const getAccentColor = (a) => a === "red" ? "#ff3131" : "#7dbff8";
  const getAccentBg = (a) => a === "red" ? "bg-[#ff3131]" : "bg-[#7dbff8]";

  const shortenLocation = (loc) => {
    return loc.replace(/^Gymtots\s+/i, "");
  };

  if (classGroups.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-[#FFFBF5]" id="classes" data-testid="classes-overview">
      <div className="container-gymtots">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>
            Our Classes
          </h2>
          <p className="text-[#636E72] text-sm max-w-md mx-auto">From their first tumble to their first cartwheel — there's a class for every stage</p>
        </div>

        <div className={`grid gap-6 ${classGroups.length >= 3 ? 'md:grid-cols-3' : classGroups.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'max-w-md mx-auto'}`}>
          {classGroups.map((group, i) => {
            const accent = accents[i % accents.length];
            const durationLabel = group.duration_minutes >= 60 ? `${group.duration_minutes / 60} hour` : `${group.duration_minutes} mins`;
            const days = [...new Set(group.sessions.map(s => s.day))].sort((a, b) => (DAY_ORDER[a] ?? 7) - (DAY_ORDER[b] ?? 7));
            const locations = [...new Set(group.sessions.map(s => shortenLocation(s.location)))];

            return (
              <div
                key={group.class_type}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                data-testid={`class-card-${group.class_type}`}
              >
                {/* Header bar */}
                <div className={`${getAccentBg(accent)} px-5 py-3 flex items-center justify-between`}>
                  <h3 className="text-white font-bold" style={{ fontFamily: 'Fredoka, cursive' }}>
                    {group.class_name}
                  </h3>
                  <span className="text-white/90 text-sm font-bold" style={{ fontFamily: 'Fredoka, cursive' }}>
                    £{group.price.toFixed(2)}
                  </span>
                </div>

                <div className="p-5">
                  <div className="flex flex-wrap gap-3 text-xs text-[#2D3436]">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" style={{ color: getAccentColor(accent) }} />
                      {durationLabel}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" style={{ color: getAccentColor(accent) }} />
                      {group.with_parent ? "With parent" : "Independent"}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" style={{ color: getAccentColor(accent) }} />
                      {days.map(d => d.slice(0, 3)).join(", ")}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" style={{ color: getAccentColor(accent) }} />
                      {locations.join(", ")}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ClassesOverview;
